import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Positioning for portaled popovers (menus, cards) anchored to a point in
// text or an element edge, in viewport coordinates.

// The anchored line's extent: popovers sit above `top`, or below `bottom` when
// there's no room above.
export interface AnchorPoint {
	left: number;
	top: number;
	bottom: number;
}

// Screen position of a character index inside a rendered text container,
// found by collapsing a Range at that offset — no marker element needed, so
// the visible text is never perturbed by measurement.
export const measureTextIndex = (
	container: HTMLElement | null,
	index: number | null
): AnchorPoint | null => {
	if (!container || index === null || index < 0) return null;
	const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
	let remaining = index;
	let node = walker.nextNode() as Text | null;
	while (node) {
		const len = node.textContent?.length ?? 0;
		if (remaining <= len) {
			const range = document.createRange();
			range.setStart(node, remaining);
			range.collapse(true);
			let rect = range.getBoundingClientRect();
			// Some engines return an empty rect for a collapsed range; fall back
			// to the right edge of the preceding character.
			if (rect.height === 0 && rect.top === 0 && remaining > 0) {
				range.setStart(node, remaining - 1);
				range.setEnd(node, remaining);
				rect = range.getBoundingClientRect();
				return { left: rect.right, top: rect.top, bottom: rect.bottom };
			}
			return { left: rect.left, top: rect.top, bottom: rect.bottom };
		}
		remaining -= len;
		node = walker.nextNode() as Text | null;
	}
	const rect = container.getBoundingClientRect();
	return { left: rect.left, top: rect.top, bottom: rect.top };
};

// Fixed-position an element just above the anchor line, flipping below it
// when the viewport top is too close, clamped horizontally to the viewport.
// Portaled elements use this to escape overflow-clipping containers. Size is
// measured after render, so the element stays hidden for its first frame;
// depKey forces a re-measure when content changes size (e.g. filtering).
export default function useAnchored(anchor: AnchorPoint | null, depKey: unknown) {
	const ref = useRef<HTMLDivElement>(null);
	const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
	const [resizeTick, setResizeTick] = useState(0);

	// Clamping reads the viewport size, so a resize must re-run placement.
	useEffect(() => {
		const onResize = () => setResizeTick((n) => n + 1);
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useLayoutEffect(() => {
		if (!anchor || !ref.current) {
			setPos(null);
			return;
		}
		const width = ref.current.offsetWidth;
		const height = ref.current.offsetHeight;
		// Clamp to the right edge first, then the left, so a popover wider than
		// the viewport pins left rather than going negative.
		const left = Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8));
		const fitsAbove = anchor.top - height - 6 >= 8;
		const next = fitsAbove
			? { left, bottom: window.innerHeight - anchor.top + 6 }
			: { left, top: anchor.bottom + 6 };
		setPos((prev) =>
			prev && prev.left === next.left && prev.top === next.top && prev.bottom === next.bottom
				? prev
				: next
		);
	}, [anchor?.left, anchor?.top, anchor?.bottom, depKey, resizeTick]);

	const style: CSSProperties = pos
		? { ...pos, visibility: 'visible' }
		: { left: 0, top: 0, visibility: 'hidden' };
	return { ref, style };
}
